if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.timeout = null;
      this.isOpen = false;
      this.cache = new Map();
      this.abortController = null;

      this.minChars = 1;
      this.debounceTime = 250;

      this.onInputHandler = this.onInput.bind(this);
      this.onKeydownHandler = this.onKeydown.bind(this);
      this.onClearHandler = this.onClear.bind(this);
      this.onDocumentClickHandler = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('.js-results-container');
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector('.js-message-error');
      this.errorMessageText = this.errorMessageContainer?.querySelector('[data-text]');
      this.drawer = this.closest('.js-drawer');

      if (!this.input || !this.resultsContainer) return;

      this.input.addEventListener('input', this.onInputHandler);
      this.addEventListener('keydown', this.onKeydownHandler);
      this.btnClearText?.addEventListener('click', this.onClearHandler);
      document.addEventListener('click', this.onDocumentClickHandler);

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', 'predictive-search-results');
      this.input.setAttribute('aria-autocomplete', 'list');
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.onInputHandler);
      this.removeEventListener('keydown', this.onKeydownHandler);
      this.btnClearText?.removeEventListener('click', this.onClearHandler);
      document.removeEventListener('click', this.onDocumentClickHandler);

      if (this.abortController) {
        this.abortController.abort();
      }
    }

    onInput() {
      clearTimeout(this.timeout);

      const searchTerm = this.input.value.trim();

      if (searchTerm.length < this.minChars) {
        this.close({ clear: false, focus: false });
        return;
      }

      this.setLoading(true);

      this.timeout = setTimeout(() => {
        this.isOpen = true;
        this.getSearchResults(searchTerm);
      }, this.debounceTime);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.input.value) {
          if (this.drawer) {
            this.drawer.parentElement.parentElement.toggleDrawer();
          }
          return;
        }

        this.close({ clear: false });
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!this.isOpen) return;

        const focusableElements = Array.from(
          this.resultsContainer.querySelectorAll('a[href], button:not([disabled])')
        );

        if (focusableElements.length === 0) return;

        const activeElement = document.activeElement;
        const currentIndex = focusableElements.indexOf(activeElement);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (activeElement === this.input) {
            focusableElements[0].focus();
          } else if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
            focusableElements[currentIndex + 1].focus();
          } else {
            focusableElements[0].focus();
          }
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (activeElement === this.input) {
            focusableElements[focusableElements.length - 1].focus();
          } else if (currentIndex === 0) {
            this.input.focus();
          } else if (currentIndex > 0) {
            focusableElements[currentIndex - 1].focus();
          } else {
            this.input.focus();
          }
        }
      }
    }

    onClear() {
      this.close({ clear: true });
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.close({ clear: false, focus: false });
      }
    }

    buildSearchUrl(searchTerm) {
      const params = new URLSearchParams();

      params.set('q', searchTerm);
      params.set('resources[type]', 'product,page,article,collection,query');
      params.set('resources[limit]', '10');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'last');
      params.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku'
      );

      params.set('section_id', 'predictive-search');

      return `${Shopify.routes.root}search/suggest?${params.toString()}`;
    }

    async getSearchResults(searchTerm) {
      try {
        const cacheKey = searchTerm.toLowerCase();

        if (this.cache.has(cacheKey)) {
          this.renderResults(this.cache.get(cacheKey));
          this.setLoading(false);
          return;
        }

        if (this.abortController) {
          this.abortController.abort();
        }

        this.abortController = new AbortController();

        const response = await fetch(this.buildSearchUrl(searchTerm), {
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Search error: ${response.status}`);
        }

        const text = await response.text();

        /**
         * Prevent old responses from replacing newer search results.
         */
        if (searchTerm !== this.input.value.trim()) {
          return;
        }

        const html = new DOMParser().parseFromString(text, 'text/html');
        const section = html.querySelector('#shopify-section-predictive-search');

        if (!section) {
          throw new Error('Predictive search section not found');
        }

        const resultsMarkup = section.innerHTML;

        this.cache.set(cacheKey, resultsMarkup);
        this.renderResults(resultsMarkup);
        this.setLoading(false);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        this.showError(error.message);
        this.setLoading(false);
      }
    }

    renderResults(resultsMarkup) {
      this.resultsContainer.innerHTML = resultsMarkup;
      this.open();
    }

    open() {
      this.isOpen = true;
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'true');
      this.btnSubmit?.setAttribute('tabindex', '-1');
      this.btnClearText?.removeAttribute('disabled');
    }

    close({ clear = true, focus = true } = {}) {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.classList.remove('is-loading');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-busy');
      this.input.value = '';

      // if (clear) {
      // }

      if (focus) {
        this.input.focus();
      }

      this.btnSubmit?.removeAttribute('tabindex');
      this.btnClearText?.setAttribute('disabled', true);
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.input.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    showError(message) {
      if (!this.isOpen) return;

      if (this.errorMessageText) {
        this.errorMessageText.textContent = message;
      }

      this.errorMessageContainer?.classList.remove('hidden');
      this.resultsContainer.classList.add('hidden');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}